import type {
  LayerBase,
  LayerStyleProperties,
  GeoDataState,
  Expression,
  SourceSpecificationUnion,
  VectorSourceSpecification,
  RasterSourceSpecification,
  RasterDemSourceSpecification,
  GeoJSONSourceSpecification,
  VideoSourceSpecification,
  ImageSourceSpecification,
} from '../../../store/types'; // Aggiunto Expression and SourceSpecificationUnion e tipi specifici per VALID_SOURCE_PROPERTIES
import { MessageSquare, MessageSquareOff, SquareFunction, Table as TableIcon, Funnel, LayoutPanelLeft, Palette, Database, Settings } from 'lucide-react'; // Added icons, Database, Settings
import { FeedbackBox } from '../../../common/FeedbackBox/FeedbackBox'; // Importa FeedbackBox - CORRETTO PERCORSO

// Importa i componenti Accordion
import { HierarchicalAccordion } from '../../../common/HierarchicalAccordion';
import { HierarchicalAccordionItem } from '../../../common/HierarchicalAccordionItem';

// Importa StylePropertyInput
import { StylePropertyInput } from '../../../common/StylePropertyInput';
import { ActionButton } from '../../../common/ActionButton'; // Importa ActionButton
import { MenuButton } from '../../../common/MenuButton'; // Importa MenuButton
import { SelectInput, type SelectOption } from '../../../common/inputs/SelectInput/SelectInput'; // AGGIUNTO
import selectInputStyles from '../../../common/inputs/SelectInput/SelectInput.module.css'; // AGGIUNTO per stile wrapper

// Importa i singoli editor di stile
import { FillLayoutProperties, FillPaintProperties } from './style-editors/FillStyleEditor';
import { LineLayoutProperties, LinePaintProperties } from './style-editors/LineStyleEditor';
import { CircleLayoutProperties, CirclePaintProperties } from './style-editors/CircleStyleEditor';
import { SymbolLayoutProperties, SymbolPaintProperties } from './style-editors/SymbolStyleEditor';
import { FillExtrusionLayoutProperties, FillExtrusionPaintProperties } from './style-editors/FillExtrusionStyleEditor'; // Modificato
import { RasterLayoutProperties, RasterPaintProperties } from './style-editors/RasterStyleEditor';
import { HeatmapLayoutProperties, HeatmapPaintProperties } from './style-editors/HeatmapStyleEditor'; // Aggiunto
import useGeoData from '../../../store/useGeoData'; // Importa l'hook di Zustand

// --- Inizio duplicazione da useLayerSynchronization.ts ---
// Interfaccia per mappare i tipi di sorgente alle loro chiavi valide
interface ValidSourcePropertiesMap {
    vector: Array<keyof VectorSourceSpecification>;
    raster: Array<keyof RasterSourceSpecification>;
    'raster-dem': Array<keyof RasterDemSourceSpecification>;
    geojson: Array<keyof GeoJSONSourceSpecification>;
    video: Array<keyof VideoSourceSpecification>;
    image: Array<keyof ImageSourceSpecification>;
}

const VALID_SOURCE_PROPERTIES: ValidSourcePropertiesMap = {
    vector: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'promoteId', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    raster: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    'raster-dem': ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'encoding', 'redFactor', 'greenFactor', 'blueFactor', 'baseShift', 'attribution', 'minzoom', 'maxzoom'],
    geojson: ['type', 'data', 'maxzoom', 'buffer', 'filter', 'tolerance', 'cluster', 'clusterRadius', 'clusterMaxZoom', 'clusterMinPoints', 'clusterProperties', 'lineMetrics', 'generateId', 'promoteId', 'attribution' /* minzoom rimosso apposta */],
    video: ['type', 'urls', 'coordinates'],
    image: ['type', 'url', 'coordinates'],
};

// Funzione helper per pulire le opzioni della sorgente
function cleanSourceOptions(sourceSpec: SourceSpecificationUnion): Record<string, any> {
    const cleanedOptions: Record<string, any> = {};
    const sourceType = sourceSpec.type;

    if (!sourceType || !(sourceType in VALID_SOURCE_PROPERTIES)) {
        console.error(`[LayerOptionsPanel/cleanSourceOptions] Tipo di sorgente non valido o mancante: ${sourceType}`);
        return { type: sourceType || 'unknown' };
    }

    const validKeys = VALID_SOURCE_PROPERTIES[sourceType];

    for (const key of validKeys) {
        if (Object.prototype.hasOwnProperty.call(sourceSpec, key)) {
            const value = (sourceSpec as any)[key];
            if (value !== undefined) {
                cleanedOptions[key as string] = value;
            }
        }
    }
    if (!cleanedOptions.type && sourceType && (sourceType in VALID_SOURCE_PROPERTIES)) {
         cleanedOptions.type = sourceType;
    }
    // minzoom per geojson source non è valido e viene gestito da VALID_SOURCE_PROPERTIES
    // Se per errore fosse ancora presente, questa ulteriore pulizia lo rimuoverebbe.
    if (cleanedOptions.type === 'geojson' && cleanedOptions.hasOwnProperty('minzoom')) {
        delete cleanedOptions.minzoom;
    }
    return cleanedOptions;
}
// --- Fine duplicazione ---

// Definizione delle props aggiornata
export interface LayerOptionsPanelProps {
  layerData: (LayerBase & LayerStyleProperties) | null; // Dati completi del layer o null
  updateLayerStyle: GeoDataState['updateLayerStyleProperty']; // Funzione per aggiornare lo stile
  openAttributeTable: GeoDataState['openAttributeTable']; // Funzione per aprire la tabella
  togglePopupEnabled: GeoDataState['togglePopupEnabled']; // Funzione per abilitare/disabilitare popup
  openManageFieldsDialog: () => void; // Funzione per aprire il dialog gestione campi
  isPopupEnabled: boolean; // Stato del popup
  isAttributeTableOpen: boolean; // Stato della tabella attributi per il layer corrente
  isStyleEditorDisabled: boolean; // Flag per disabilitare i controlli
}

const LayerOptionsPanel: React.FC<LayerOptionsPanelProps> = ({
  layerData,
  updateLayerStyle,
  openAttributeTable,
  togglePopupEnabled,
  openManageFieldsDialog,
  isPopupEnabled,
  isAttributeTableOpen,
  isStyleEditorDisabled,
}) => {
  // const dispatch = useAppDispatch(); // Rimosso: non necessario con Zustand per questa azione
  const updateLayerProperties = useGeoData(state => (state as GeoDataState).updateLayerProperties);
  const resetLayerStyleToDefault = useGeoData(state => (state as GeoDataState).resetLayerStyleToDefault);
  const updateLayerSourceTileSize = useGeoData((state: GeoDataState) => state.updateLayerSourceTileSize); // AGGIUNTO

  // Handler per aprire la tabella attributi
  const handleOpenAttributeTable = () => {
    if (layerData) { // Controlla se layerData è valido
      openAttributeTable(layerData.id);
    }
  };

  // Funzione per formattare il tipo di layer per la visualizzazione
  const formatLayerType = (type: string | undefined): string => {
    if (!type) return '';
    // Esempio di formattazione: 'fill-extrusion' -> 'Fill Extrusion', 'geojson' -> 'GeoJSON'
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('Geojson', 'GeoJSON'); // Caso specifico per GeoJSON
  };

  // Funzione helper per renderizzare l'editor corretto (invariata)
  const renderStyleEditor = () => {
    // ... (codice invariato) ...
    if (!layerData || !('maplibreLayerType' in layerData)) {
      if (!isStyleEditorDisabled) {
          console.error(`LayerOptionsPanel: Layer data is missing or incomplete even though editor should be enabled.`);
          return <div>Error: Layer data not found or incomplete.</div>;
      }
      return null;
    }
    if (!updateLayerStyle) {
      console.error("LayerOptionsPanel: updateLayerStyle function is missing.");
      return <div>Error: Critical update function missing.</div>;
    }
    // NOTA: Questa funzione renderStyleEditor ora è usata solo come fallback
    // o per tipi di layer non ancora refattorizzati.
    // La logica di rendering per tutti i tipi di layer refattorizzati è gestita direttamente negli accordion.
    switch (layerData.maplibreLayerType) {
      default:
        // Questo fallback ora dovrebbe essere per tipi di layer veramente non supportati o non ancora aggiunti al sistema.
        // Se un tipo è stato refattorizzato ma manca qui, è un errore.
        if (layerData.maplibreLayerType !== 'fill' &&
            layerData.maplibreLayerType !== 'line' &&
            layerData.maplibreLayerType !== 'circle' &&
            layerData.maplibreLayerType !== 'symbol' &&
            layerData.maplibreLayerType !== 'raster' &&
            layerData.maplibreLayerType !== 'fill-extrusion') {
          return <div>Unsupported layer type: {layerData.maplibreLayerType}</div>;
        }
        return null; // Se è un tipo refattorizzato, il rendering avviene negli accordion.
    }
  };

  // Classe CSS condizionale per l'intero pannello se necessario
  const panelClassName = `layer-options-panel ${isStyleEditorDisabled ? 'content-disabled' : ''}`;

  return (
    <div className={panelClassName}>
      {/* Header con nome layer e azioni */}
      {layerData && (
        <>
          <div
            className="layer-style-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxHeight: '40px'
            }}
          >
            <h3 className="layer-style-header__name-container">
              <span className="layer-style-header__name" title={layerData.name}>
                {layerData.name}
              </span>
              {layerData.maplibreLayerType && (
                <span className="layer-type-badge">
                  {formatLayerType(layerData.maplibreLayerType)}
                </span>
              )}
            </h3>
          </div>
          {/* Contenitore per i bottoni di azione */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ActionButton
              icon={TableIcon}
              text="Table"
              onClick={handleOpenAttributeTable}
              title="Apri Tabella Attributi"
              isActive={isAttributeTableOpen} // Aggiunta prop isActive
              disabled={isStyleEditorDisabled}
              style={{ marginRight: 'var(--spacing-m)' }} // Aggiunto marginRight
            />
            <ActionButton
              icon={MessageSquareOff}
              activeIcon={MessageSquare}
              text="Popup"
              onClick={() => togglePopupEnabled(layerData.id)}
              isActive={isPopupEnabled}
              title={isPopupEnabled ? "Disabilita Popup al Click" : "Abilita Popup al Click"}
              disabled={isStyleEditorDisabled}
              style={{ marginRight: 'var(--spacing-m)' }} // Aggiunto marginRight
            />
            <ActionButton
              icon={SquareFunction}
              text="Fields"
              onClick={openManageFieldsDialog}
              title={isPopupEnabled ? "Gestisci Campi Visibili nel Popup" : "Abilita prima il Popup per gestire i campi"}
              disabled={isStyleEditorDisabled || !isPopupEnabled}
            />
          </div>
        </>
      )}

      {/* Contenitore Editor Stile e JSON */}
      {layerData && !isStyleEditorDisabled && (
        <div className="layer-style-editor-container">
          <HierarchicalAccordion style={{ width: '100%' }} defaultOpenId={layerData?.sourceSpecification ? `source-options-main-${layerData.id}` : `filter-zoom-${layerData.id}`}>
            {/* NUOVO ACCORDION PER OPZIONI SORGENTE (SPOSTATO IN CIMA) */}
            {layerData?.sourceSpecification && (
              <HierarchicalAccordionItem
                title="Source Options" // Etichetta aggiornata
                level={1}
                id={`source-options-main-${layerData.id}`} // ID aggiornato
                icon={Database}
              >
                {/* Contenuto dell'accordion "Source Options" - ora contiene un HierarchicalAccordion annidato */}
                <HierarchicalAccordion style={{ width: '100%' }}> {/* Rimossa prop allowMultiple e corretto tag di chiusura più avanti */}
                  <HierarchicalAccordionItem
                    title="Zoom & Filter (Source)" // title ripristinato
                    level={2}
                    id={`source-zoom-filter-${layerData.id}`}
                    icon={Funnel} // icon ripristinato
                  >
                    <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                      {(() => {
                        if (!layerData?.sourceSpecification) return null;

                        const { sourceSpecification } = layerData;
                      const sourceType = sourceSpecification.type;

                      const showSourceMinzoomControl =
                        sourceType === 'vector' ||
                        sourceType === 'raster' ||
                        sourceType === 'raster-dem';

                      const showSourceMaxzoomControl =
                        sourceType === 'vector' ||
                        sourceType === 'raster' ||
                        sourceType === 'raster-dem' ||
                        sourceType === 'geojson';

                      const showSourceFilterControl = sourceType === 'geojson';

                      return (
                        <>
                          {showSourceMinzoomControl && (
                            <StylePropertyInput
                              layerId={layerData.id}
                              propertyName={'minzoom' as any}
                              label="Min Zoom (Source)"
                              currentValue={(sourceSpecification as import('../../../store/types').TileSourceProps).minzoom ?? 0}
                              propertyType="range"
                              isExpressionPossible={false}
                              onStyleChange={(id, _propName, value) => {
                                if (layerData?.sourceSpecification) {
                                  const updatedSpec = {
                                    ...layerData.sourceSpecification,
                                    minzoom: value as number,
                                  } as SourceSpecificationUnion;
                                  const cleanedSpec = cleanSourceOptions(updatedSpec);
                                  updateLayerProperties({
                                    id,
                                    properties: {
                                      sourceSpecification: cleanedSpec as SourceSpecificationUnion,
                                    },
                                  });
                                }
                              }}
                              numberConfig={{ min: 0, max: 24, step: 1 }}
                              disabled={isStyleEditorDisabled}
                            />
                          )}
                          {showSourceMaxzoomControl && (
                            <StylePropertyInput
                              layerId={layerData.id}
                              propertyName={'maxzoom' as any}
                              label="Max Zoom (Source)"
                              currentValue={(sourceSpecification as import('../../../store/types').TileSourceProps | import('../../../store/types').GeoJSONSourceSpecification).maxzoom ?? 24}
                              propertyType="range"
                              isExpressionPossible={false}
                              onStyleChange={(id, _propName, value) => {
                                if (layerData?.sourceSpecification) {
                                  const updatedSpec = {
                                    ...layerData.sourceSpecification,
                                    maxzoom: value as number,
                                  } as SourceSpecificationUnion;
                                  const cleanedSpec = cleanSourceOptions(updatedSpec);
                                  updateLayerProperties({
                                    id,
                                    properties: {
                                      sourceSpecification: cleanedSpec as SourceSpecificationUnion,
                                    },
                                  });
                                }
                              }}
                              numberConfig={{ min: 0, max: 24, step: 1 }}
                              disabled={isStyleEditorDisabled}
                            />
                          )}
                          {showSourceFilterControl && (
                            <StylePropertyInput
                              layerId={layerData.id}
                              propertyName={'filter' as any}
                              label="Filtro (Source)"
                              currentValue={(sourceSpecification as import('../../../store/types').GeoJSONSourceSpecification).filter as Expression || null}
                              propertyType="text"
                              isExpressionPossible={true}
                              availablePropertyKeys={layerData.availablePropertyKeys || []}
                              onStyleChange={(id, _propName, value) => {
                                if (layerData?.sourceSpecification) {
                                  const updatedSpec = {
                                    ...layerData.sourceSpecification,
                                    filter: value as Expression,
                                  } as SourceSpecificationUnion; // Cast a SourceSpecificationUnion
                                  const cleanedSpec = cleanSourceOptions(updatedSpec);
                                  updateLayerProperties({
                                    id,
                                    properties: {
                                      sourceSpecification: cleanedSpec as SourceSpecificationUnion,
                                    },
                                  });
                                }
                              }}
                              disabled={isStyleEditorDisabled}
                              className="style-property--full-width"
                            />
                          )}
                          {!showSourceMinzoomControl && !showSourceMaxzoomControl && !showSourceFilterControl && (
                            <p>Nessuna opzione di Zoom o Filtro disponibile per il tipo di sorgente '{formatLayerType(sourceType)}'.</p>
                          )}
                        </>
                      );
                    })()}
                    </div>
                  </HierarchicalAccordionItem>

                  <HierarchicalAccordionItem
                    title="Advanced Source Properties" // title ripristinato
                    level={2}
                    id={`source-advanced-props-${layerData.id}`}
                    icon={Settings} // icon ripristinato
                  >
                    <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                      {(() => {
                        if (!layerData?.sourceSpecification) return null;
                        const { sourceSpecification } = layerData;
                      const sourceType = sourceSpecification.type;

                      const handleSourceSpecChange = (propertyName: string, value: any) => {
                        if (layerData?.sourceSpecification) { // Ensure sourceSpecification exists
                          const currentSourceSpec = layerData.sourceSpecification;
                          const updatedSpec = {
                            ...currentSourceSpec,
                            [propertyName]: value,
                          } as SourceSpecificationUnion;
                          const cleanedSpec = cleanSourceOptions(updatedSpec);
                          updateLayerProperties({
                            id: layerData.id,
                            properties: {
                              sourceSpecification: cleanedSpec as SourceSpecificationUnion,
                            },
                          });
                        }
                      };

                      // Attribution is common to types that extend MapLibreSourceBase
                      const showAttributionControl =
                        sourceType === 'vector' ||
                        sourceType === 'raster' ||
                        sourceType === 'raster-dem' ||
                        sourceType === 'geojson';

                      const commonProps = showAttributionControl ? (
                        <StylePropertyInput
                          layerId={layerData.id}
                          propertyName={'attribution' as any}
                          label="Attribution (Source)"
                          currentValue={(sourceSpecification as import('../../../store/types').MapLibreSourceBase).attribution || ''}
                          propertyType="text"
                          isExpressionPossible={false}
                          onStyleChange={(_id, _propName, val) => handleSourceSpecChange('attribution', val)}
                          disabled={isStyleEditorDisabled}
                          className="style-property--full-width"
                        />
                      ) : null;

                      let specificProps = null;

                      if (sourceType === 'vector' || sourceType === 'raster' || sourceType === 'raster-dem') {
                        const tileSourceSpec = sourceSpecification as import('../../../store/types').TileSourceProps;
                        specificProps = (
                          <>
                            <div className="style-control" style={{ marginTop: 'var(--spacing-s)' }}>
                              <label htmlFor={`source-scheme-${layerData.id}`} className="style-label">Scheme (Source)</label>
                              <div className="style-control-input-wrapper">
                                <SelectInput
                                  id={`source-scheme-${layerData.id}`}
                                  value={tileSourceSpec.scheme || 'xyz'}
                                  options={[
                                    { value: 'xyz', label: 'xyz' },
                                    { value: 'tms', label: 'tms' },
                                  ]}
                                  onChange={(e) => handleSourceSpecChange('scheme', e.target.value)}
                                  disabled={isStyleEditorDisabled}
                                  className={selectInputStyles.select}
                                />
                              </div>
                            </div>
                            <div className="style-control" style={{ display: 'flex', alignItems: 'center', marginTop: 'var(--spacing-s)' }}> {/* Anche se non richiesto, per coerenza potremmo aggiornare anche questo, ma mi attengo alla richiesta specifica per ora */}
                              <input
                                type="checkbox"
                                id={`source-volatile-${layerData.id}`}
                                checked={tileSourceSpec.volatile || false}
                                onChange={(e) => handleSourceSpecChange('volatile', e.target.checked)}
                                disabled={isStyleEditorDisabled}
                                style={{ marginRight: 'var(--spacing-xs)' }}
                              />
                              <label htmlFor={`source-volatile-${layerData.id}`}>Volatile (Source)</label>
                            </div>
                            {/* Vecchia visualizzazione bounds rimossa da qui */}
                          </>
                        );
                      }

                      if (sourceType === 'raster' || sourceType === 'raster-dem') {
                        const rasterSourceSpec = sourceSpecification as RasterSourceSpecification | RasterDemSourceSpecification;
                        const currentTileSize = rasterSourceSpec?.tileSize ?? 256;
                        const tileSizeOptions: SelectOption[] = [
                          { value: 128, label: '128px' },
                          { value: 256, label: '256px' },
                          { value: 512, label: '512px' },
                        ];
                        const handleTileSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
                          const newSize = parseInt(event.target.value, 10);
                          if (layerData && layerData.id) {
                            updateLayerSourceTileSize(layerData.id, newSize);
                          }
                        };
                        const bounds = rasterSourceSpec?.bounds;

                        specificProps = (
                          <>
                            {specificProps} {/* Mantieni le props precedenti se ce ne sono (es. da vector) */}
                            <div className="style-control" style={{ marginTop: 'var(--spacing-s)' }}>
                              <label htmlFor={`source-tilesize-${layerData.id}`} className="style-label">TILE SIZE (SOURCE)</label>
                              <div className="style-control-input-wrapper">
                                <SelectInput
                                  id={`source-tilesize-${layerData.id}`}
                                  value={currentTileSize.toString()}
                                  options={tileSizeOptions}
                                  onChange={handleTileSizeChange}
                                  disabled={isStyleEditorDisabled}
                                  className={selectInputStyles.select}
                                />
                              </div>
                            </div>
                            <div style={{ marginTop: 'var(--spacing-s)' }}> {/* Wrapper div per lo stile */}
                              <FeedbackBox
                                statusType="info"
                                title="Limiti Sorgente"
                                message={bounds ? bounds.join(', ') : 'Non definiti'}
                              />
                            </div>
                          </>
                        );
                      }

                      if (sourceType === 'geojson') {
                        const geojsonSourceSpec = sourceSpecification as import('../../../store/types').GeoJSONSourceSpecification;
                        specificProps = (
                          <>
                            <StylePropertyInput
                              layerId={layerData.id}
                              propertyName={'buffer' as any}
                              label="Buffer (Source)"
                              currentValue={geojsonSourceSpec.buffer ?? 128}
                              propertyType="range"
                              isExpressionPossible={false}
                              onStyleChange={(_id, _propName, val) => handleSourceSpecChange('buffer', val as number)}
                              numberConfig={{ min: 0, max: 1024, step: 1 }}
                              disabled={isStyleEditorDisabled}
                            />
                            <StylePropertyInput
                              layerId={layerData.id}
                              propertyName={'tolerance' as any}
                              label="Tolerance (Source)"
                              currentValue={geojsonSourceSpec.tolerance ?? 0.375}
                              propertyType="range" // Assuming number input, range might be too restrictive
                              isExpressionPossible={false}
                              onStyleChange={(_id, _propName, val) => handleSourceSpecChange('tolerance', val as number)}
                              numberConfig={{ min: 0, max: 10, step: 0.001 }}
                              disabled={isStyleEditorDisabled}
                            />
                            <div className="style-property-input-container" style={{ display: 'flex', alignItems: 'center', marginTop: 'var(--spacing-s)' }}>
                              <input
                                type="checkbox"
                                id={`source-generateid-${layerData.id}`}
                                checked={geojsonSourceSpec.generateId || false}
                                onChange={(e) => handleSourceSpecChange('generateId', e.target.checked)}
                                disabled={isStyleEditorDisabled}
                                style={{ marginRight: 'var(--spacing-xs)' }}
                              />
                              <label htmlFor={`source-generateid-${layerData.id}`}>Generate IDs (Source)</label>
                            </div>
                            {geojsonSourceSpec.cluster && (
                              <>
                                <StylePropertyInput
                                  layerId={layerData.id}
                                  propertyName={'clusterRadius' as any}
                                  label="Cluster Radius (Source)"
                                  currentValue={geojsonSourceSpec.clusterRadius ?? 50} // Default example
                                  propertyType="range"
                                  isExpressionPossible={false}
                                  onStyleChange={(_id, _propName, val) => handleSourceSpecChange('clusterRadius', val as number)}
                                  numberConfig={{ min: 0, max: 500, step: 1 }} // Example range
                                  disabled={isStyleEditorDisabled}
                                />
                                <StylePropertyInput
                                  layerId={layerData.id}
                                  propertyName={'clusterMaxZoom' as any}
                                  label="Cluster Max Zoom (Source)"
                                  currentValue={geojsonSourceSpec.clusterMaxZoom ?? 14} // Default example
                                  propertyType="range"
                                  isExpressionPossible={false}
                                  onStyleChange={(_id, _propName, val) => handleSourceSpecChange('clusterMaxZoom', val as number)}
                                  numberConfig={{ min: 0, max: 24, step: 1 }}
                                  disabled={isStyleEditorDisabled}
                                />
                                <StylePropertyInput
                                  layerId={layerData.id}
                                  propertyName={'clusterMinPoints' as any}
                                  label="Cluster Min Points (Source)"
                                  currentValue={geojsonSourceSpec.clusterMinPoints ?? 2} // Default example
                                  propertyType="range"
                                  isExpressionPossible={false}
                                  onStyleChange={(_id, _propName, val) => handleSourceSpecChange('clusterMinPoints', val as number)}
                                  numberConfig={{ min: 2, max: 100, step: 1 }}
                                  disabled={isStyleEditorDisabled}
                                />
                              </>
                            )}
                          </>
                        );
                      }

                      return (
                        <>
                          {commonProps}
                          {specificProps}
                          {!specificProps && sourceType !== 'video' && sourceType !== 'image' && ( // Video and Image have no advanced props here yet
                             <p>Nessuna opzione avanzata di sorgente disponibile per il tipo '{formatLayerType(sourceType)}'.</p>
                          )}
                        </>
                      );
                    })()}
                    </div>
                  </HierarchicalAccordionItem>

                </HierarchicalAccordion> {/* Chiusura del HierarchicalAccordion annidato */}
              </HierarchicalAccordionItem>
            )}


            <HierarchicalAccordionItem
              title="Layout"
              level={1}
              id={`layout-options-${layerData.id}`}
              icon={LayoutPanelLeft} // Added icon
            >
              <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                {layerData.maplibreLayerType === 'fill' && (
                  <FillLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'line' && (
                  <LineLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'circle' && (
                  <CircleLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'symbol' && (
                  <SymbolLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                    availablePropertyKeys={layerData.availablePropertyKeys}
                  />
                )}
                {layerData.maplibreLayerType === 'raster' && (
                  <RasterLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'fill-extrusion' && (
                  <FillExtrusionLayoutProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'heatmap' && (
                  <HeatmapLayoutProperties
                    layer={layerData as any}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                    availablePropertyKeys={layerData.availablePropertyKeys}
                  />
                )}
                {/* Altri tipi di layer per Layout verranno aggiunti qui */}
                {layerData.maplibreLayerType !== 'fill' && layerData.maplibreLayerType !== 'line' && layerData.maplibreLayerType !== 'circle' && layerData.maplibreLayerType !== 'symbol' && layerData.maplibreLayerType !== 'raster' && layerData.maplibreLayerType !== 'fill-extrusion' && layerData.maplibreLayerType !== 'heatmap' && (
                  <p>Opzioni di Layout per {layerData.maplibreLayerType} (da implementare)</p>
                )}
              </div>
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem
              title="Paint"
              level={1}
              id={`paint-options-${layerData.id}`}
              icon={Palette} // Added icon
            >
              <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                {layerData.maplibreLayerType === 'fill' && (
                  <FillPaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'line' && (
                  <LinePaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'circle' && (
                  <CirclePaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'symbol' && (
                  <SymbolPaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                    availablePropertyKeys={layerData.availablePropertyKeys}
                  />
                )}
                {layerData.maplibreLayerType === 'raster' && (
                  <RasterPaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'fill-extrusion' && (
                  <FillExtrusionPaintProperties
                    layer={layerData}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                  />
                )}
                {layerData.maplibreLayerType === 'heatmap' && (
                  <HeatmapPaintProperties
                    layer={layerData as any}
                    updateLayerStyle={updateLayerStyle}
                    isDisabled={isStyleEditorDisabled}
                    availablePropertyKeys={layerData.availablePropertyKeys}
                  />
                )}
                {/* Fallback per tipi di layer non ancora refattorizzati/sconosciuti */}
                {layerData.maplibreLayerType !== 'fill' &&
                 layerData.maplibreLayerType !== 'line' &&
                 layerData.maplibreLayerType !== 'circle' &&
                 layerData.maplibreLayerType !== 'symbol' &&
                 layerData.maplibreLayerType !== 'raster' &&
                 layerData.maplibreLayerType !== 'fill-extrusion' &&
                 layerData.maplibreLayerType !== 'heatmap' &&
                 renderStyleEditor()}
              </div>
            </HierarchicalAccordionItem>
          </HierarchicalAccordion>
          {/* Reset to default style button */}
          {layerData && (
            <MenuButton
              onClick={() => {
                if (layerData && layerData.id) {
                  resetLayerStyleToDefault(layerData.id); // Chiama direttamente l'azione Zustand
                }
              }}
              disabled={!layerData} // Se layerData è null, questo componente non viene renderizzato.
                                    // Se è renderizzato, layerData esiste, quindi !layerData è false.
              // La prop 'style' non è supportata, usare 'className' per stili personalizzati se necessario.
            >
              Reset to default style
            </MenuButton>
          )}
        </div>
      )}
    </div>
  );
};

export default LayerOptionsPanel;